<template>
    <b-list-group class="list-group-accent">
        <b-list-group-item
            class="list-group-item-accent-secondary bg-light text-center font-weight-bold text-muted text-uppercase small"
        >
            {{ $t('profile.subordinates.listOfSubordinates') }}
        </b-list-group-item>
        <b-list-group-item class="list-group-item-accent-light">
            <div class="text-muted text-uppercase title font-weight-bold small">
                {{ $t('profile.subordinates.description') }}
            </div>
            <small class="text-muted mr-3">
                <i class="fa fa-angle-double-right fa-lg mr-2"></i>
                {{ $t('profile.subordinates.explanation') }}
            </small>
            <div class="mt-3">
                <loader :loader="loader" v-if="loader" />
                <div v-else>
                    <b-table
                        hover
                        striped
                        bordered
                        small
                        :items="items"
                        :fields="fields"
                        :busy="loader"
                        v-if="items.length"
                    >
                        <template #cell(picture)="{ item }">
                            <b-link :to="`/v2/profile/${item.id}`">
                                <avatar class="d-inline-block" :src="item.picture" :username="item.name" :size="35" />
                            </b-link>
                        </template>
                        <template #cell(name)="{ item }">
                            <b-link :to="`/v2/profile/${item.id}`">
                                <strong>{{ item.name }}</strong>
                            </b-link>
                        </template>
                        <template #cell(status)="{ item }">
                            <b-badge
                                pill
                                :style="{
                                    background: item.status.color,
                                    color: '#FFFFFF',
                                }"
                                v-b-popover.hover="$t('status.' + item.status.name + '.description')"
                            >
                                {{ $t('status.' + item.status.name + '.name') }}</b-badge
                            >
                        </template>
                        <template #cell(isAllowedToAccessManagerProfile)="{ item }">
                            <label class="switch switch-pill switch-label switch-primary m-0">
                                <input
                                    type="checkbox"
                                    class="switch-input"
                                    @change="(event) => onChangeStatusHandler(item.id, item.name, event.target.checked)"
                                    v-model="data[item.id]"
                                />
                                <span
                                    class="switch-slider"
                                    :data-checked="$t('form.switch.yes')"
                                    :data-unchecked="$t('form.switch.no')"
                                />
                            </label>
                        </template>
                        <template #cell(network)="{ item }">
                            <b-button
                                class="mr-2"
                                variant="secondary"
                                size="sm"
                                @click="$router.push('/v2/values/valuers/' + item.id)"
                                >{{ $t('people.viewNetwork') }}</b-button
                            >
                        </template>
                    </b-table>
                    <b-alert show variant="info" v-else>
                        {{ $t('profile.subordinates.emptySubordinates') }}
                    </b-alert>
                </div>
            </div>
        </b-list-group-item>
    </b-list-group>
</template>

<script>
import { getSubordinates, updateHasPermissionToAccessManagerProfile } from './api'
import i18n from '@/i18n'
import Avatar from 'vue-avatar'
import notification, { success, danger } from '@/lib/notification'
import Swal from 'sweetalert2'
import Loader from '@/views/components/loader'

notification.init()

export default {
    name: 'Subordinates',
    components: {
        Avatar,
        Loader,
    },
    props: {
        user: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            fields: [
                this.getField('picture', 'picture'),
                this.getField('name', 'name'),
                this.getField('email', 'email'),
                this.getField('status', 'status'),
                this.getField('isAllowedToAccessManagerProfile', 'permission'),
                this.getNetworkField(),
            ],
            items: [],
            data: {},
            loader: false,
        }
    },
    methods: {
        getField(key, label) {
            return {
                key,
                label: i18n.t(`profile.subordinates.${label}`),
                class: 'text-center align-middle',
            }
        },
        getNetworkField() {
            if (!this.userHasValues()) return null
            return {
                key: 'network',
                label: i18n.t(`profile.subordinates.network`),
                class: 'text-center align-middle',
            }
        },
        userHasValues() {
            return this.$store.state.currentUser.user.has_value && this.$store.state.currentUser.user.company.has_value
        },
        onChangeStatusHandler(userId, username, status) {
            Swal.fire({
                title: i18n.t('notification.areYouSure'),
                text: i18n.t('notification.updateStatus', { user: username }),
                type: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#3085d6',
                cancelButtonColor: '#d33',
                confirmButtonText: i18n.t('notification.yes'),
            }).then((result) => {
                if (result.value) {
                    updateHasPermissionToAccessManagerProfile(userId, status)
                        .then(() => {
                            this.notificationStatusUpdatedSuccessfully()
                        })
                        .catch(() => {
                            this.notificationError()
                        })
                } else {
                    this.data[userId] = !status
                }
            })
        },
        notificationStatusUpdatedSuccessfully() {
            success(
                i18n.t('notification.updatedSuccessfully', {
                    item: i18n.t('profile.subordinates.status'),
                })
            )
        },
        notificationError() {
            danger()
        },
        getSubordinates(userId) {
            this.loader = true
            getSubordinates(userId)
                .then((resp) => {
                    const { data } = resp
                    this.items = data
                    this.data = data.reduce((object, user) => {
                        return {
                            ...object,
                            ...{
                                [user.id]: !!user.is_allowed_to_access_manager_profile,
                            },
                        }
                    }, {})
                })
                .finally(() => {
                    this.loader = false
                })
        },
    },
    computed: {
        currentUser() {
            return this.$store.state.currentUser.user
        },
    },
    watch: {
        user: {
            deep: true,
            immediate: true,
            handler() {
                this.getSubordinates(this.user.id)
            },
        },
    },
}
</script>
